/**
 * Sparrow Forge Score — calibrated penetration + flight quality score.
 *
 * Formula (from docs/methods-reframe-ashby-breaks.md §12):
 *
 *   ForgeScore = 40 × tune_quality_score
 *              + 25 × momentum_score
 *              + 20 × effective_foc_penetration_score
 *              + 15 × energy_retention_at_distance_score
 *
 * Tier cutoffs: Aerospace ≥90 · Surgical 75-89 · Sharp 60-74 · Tuned <60.
 *
 * Calibrated against 6 documented field hunts (Field Notes appendix).
 */

import type { Build, Environment, Distance, ShotScenario, TrajectoryResult, PointType } from '../trajectory/types';
import { solveTrajectory } from '../trajectory';
import { deflectionProbability, DEFLECTION_OUTCOME_PENETRATION } from './deflection';
import { getSpecies, type SpeciesProfile } from './speciesProfiles';

export type Tier = 'Aerospace' | 'Surgical' | 'Sharp' | 'Tuned';

export interface ForgeScoreResult {
  score: number;
  tier: Tier;
  components: {
    tuneQuality: number;
    momentum: number;
    effectiveFocPenetration: number;
    energyRetention: number;
  };
  /** Effective per-axis weights (in percent, summing to 100) actually used to
   *  compute this score. Without a species, this is {40, 25, 20, 15}. With a
   *  species, the momentum and retention weights are biased by the species'
   *  momentumBias and retentionBias, and the tune + foc weights are pulled
   *  down proportionally so the total stays at 100. Surfaced so the UI can
   *  show "this score is computed with momentum at 45% for cape buffalo." */
  effectiveWeights: {
    tuneQuality: number;
    momentum: number;
    effectiveFocPenetration: number;
    energyRetention: number;
  };
  source: 'opencv' | 'slider' | 'default';
  trajectory: TrajectoryResult;
  /** One-sentence diagnostic naming the weakest axis and the next lever to move. */
  diagnostic: string;
  /** The named weakest axis */
  weakestAxis: 'tuneQuality' | 'momentum' | 'effectiveFocPenetration' | 'energyRetention';
  /** Effective KE at impact — broken down into the four loss components.
   *  Fletched shows a single "Eff. KE" that doesn't move. We surface the
   *  actual budget so the user sees where the energy is going. */
  energyBudget: {
    launchKeFtLb: number;
    dragLossFtLb: number;
    windShearLossFtLb: number;
    tuneYawLossFtLb: number;
    deflectionRiskLossFtLb: number;
    effectiveKeAtImpactFtLb: number;
  };
}

/**
 * Tune quality penetration curve — calibrated against Field Notes A.1, A.3,
 * A.4, A.6. Returns 0-1 multiplier on effective penetration.
 */
export function tuneQualityFactor(yawDeg: number): number {
  if (yawDeg < 2) return 1.0;
  if (yawDeg < 4) return 1.0 - 0.015 * (yawDeg - 2);  // → 0.97 at 4°
  if (yawDeg < 7) return 0.97 - 0.030 * (yawDeg - 4); // → 0.88 at 7°
  if (yawDeg < 12) return 0.88 - 0.050 * (yawDeg - 7); // → 0.63 at 12°
  return Math.max(0.63 - 0.075 * (yawDeg - 12), 0.25);
}

/**
 * Momentum score — normalized against a representative hunting baseline
 * (500gr at 270fps = baseline 1.0 score).
 */
export function momentumScore(grains: number, fps: number): number {
  const GRAINS_PER_SLUG = 7000 * 32.174;
  const massSlug = grains / GRAINS_PER_SLUG;
  const momentum = massSlug * fps;
  const reference = (500 / GRAINS_PER_SLUG) * 270;
  return Math.min(momentum / reference, 1.2);
}

/**
 * Effective FOC penetration — diminishing returns above 15%.
 */
export function effectiveFocPenetration(focPercent: number): number {
  if (focPercent <= 9) return 0.5 + (focPercent / 9) * 0.3;     // 0.5-0.8
  if (focPercent <= 15) return 0.8 + ((focPercent - 9) / 6) * 0.15; // 0.8-0.95
  if (focPercent <= 20) return 0.95 + ((focPercent - 15) / 5) * 0.05; // 0.95-1.0
  return 1.0 - ((focPercent - 20) / 5) * 0.05;                  // diminish above 20
}

export function tierFromScore(score: number): Tier {
  if (score >= 90) return 'Aerospace';
  if (score >= 75) return 'Surgical';
  if (score >= 60) return 'Sharp';
  return 'Tuned';
}

export interface ScoreInputs {
  build: Build;
  env: Environment;
  dist: Distance;
}

/**
 * Base per-axis weights, in percent. Sum to 100.
 *   tune 40, momentum 25, foc 20, retention 15
 */
const BASE_WEIGHTS = { tune: 40, mom: 25, foc: 20, ret: 15 } as const;

/**
 * Bone-defeating penetration multiplier — applied to the momentum component
 * for dangerous game (cape buffalo, hippo, elephant) where heavy-bone
 * penetration physics dominates the outcome. Mirrors the Ashby paradigm:
 * single-bevel COC fixed heads get a bone-defeating bonus; mechanical heads
 * are heavily penalized because they pivot, deflect, and bleed energy at
 * the bone interface.
 */
function bonePenetrationMultiplier(
  pointType: PointType,
  gameMassClass: SpeciesProfile['gameMassClass']
): number {
  if (gameMassClass !== 'dangerous') return 1.0;
  switch (pointType) {
    case 'single-bevel':
      return 1.20; // Ashby bone-defeating geometry — best heavy-bone result
    case 'COC fixed':
      return 1.12; // fixed cut-on-contact — strong, no rotational split
    case 'fixed mech':
      return 0.78; // partial blades pivot at bone
    case 'hybrid mech':
      return 0.72;
    case 'full mech':
      return 0.55; // severe deflection / blade collapse on heavy bone
  }
}

/**
 * Resolve the species profile from the environment, if a species tag is set.
 */
function resolveSpecies(env: Environment): SpeciesProfile | undefined {
  return env.speciesTag ? getSpecies(env.speciesTag) : undefined;
}

/**
 * Compute the effective per-axis weights for a given species, applying its
 * momentumBias and retentionBias on top of BASE_WEIGHTS. The bias is added
 * to its own axis and pulled proportionally from the tune + foc axes so
 * the total stays at 100. Floor: tune ≥ 22, foc ≥ 10, mom ≥ 12, ret ≥ 5.
 */
function effectiveAxisWeights(species: SpeciesProfile | undefined): {
  tune: number; mom: number; foc: number; ret: number;
} {
  if (!species) {
    return { tune: BASE_WEIGHTS.tune, mom: BASE_WEIGHTS.mom, foc: BASE_WEIGHTS.foc, ret: BASE_WEIGHTS.ret };
  }
  let mom = Math.max(12, BASE_WEIGHTS.mom + species.momentumBias);
  let ret = Math.max(5, BASE_WEIGHTS.ret + species.retentionBias);
  const remaining = Math.max(32, 100 - mom - ret);
  const baseTuneFocTotal = BASE_WEIGHTS.tune + BASE_WEIGHTS.foc;
  const tuneShare = BASE_WEIGHTS.tune / baseTuneFocTotal;
  const focShare = BASE_WEIGHTS.foc / baseTuneFocTotal;
  let tune = Math.max(22, remaining * tuneShare);
  let foc = Math.max(10, remaining * focShare);
  /* Renormalize to exactly 100 in case of floor activation */
  const total = tune + mom + foc + ret;
  const scale = 100 / total;
  return {
    tune: tune * scale,
    mom: mom * scale,
    foc: foc * scale,
    ret: ret * scale,
  };
}

export function computeForgeScore(inputs: ScoreInputs): ForgeScoreResult {
  const traj = solveTrajectory(inputs.build, inputs.env, inputs.dist);
  const species = resolveSpecies(inputs.env);

  /* 1. Tune quality component */
  const tuneFactor = tuneQualityFactor(traj.impactYawDeg);
  const tuneComponent = tuneFactor * 100;

  /* 2. Momentum component — with bone-penetration multiplier for dangerous game */
  const baseMomFactor = momentumScore(
    inputs.build.arrowGrains,
    Math.sqrt((2 * traj.keFtLb) / (inputs.build.arrowGrains / (7000 * 32.174)))
  );
  const boneMult = species
    ? bonePenetrationMultiplier(inputs.build.pointType, species.gameMassClass)
    : 1.0;
  const momComponent = Math.min(100, baseMomFactor * boneMult * 100);

  /* 3. Effective FOC penetration component — modified by deflection */
  const baseFoc = effectiveFocPenetration(inputs.build.fOCPercent);
  const pDeflect = deflectionProbability(inputs.env.shotScenario, inputs.build.pointType);
  const deflectOutcomePen = DEFLECTION_OUTCOME_PENETRATION[inputs.build.pointType];
  const focComponent = baseFoc * ((1 - pDeflect) + pDeflect * deflectOutcomePen) * 100;

  /* 4. Energy retention at distance component */
  const retentionComponent = traj.retainedKePct * 100;

  /* Species-biased weighted sum */
  const w = effectiveAxisWeights(species);
  const rawScore =
    (w.tune * tuneComponent +
      w.mom * momComponent +
      w.foc * focComponent +
      w.ret * retentionComponent) /
    100;
  const score = Math.min(100, Math.max(0, Math.round(rawScore)));

  const components = {
    tuneQuality: Math.round(tuneComponent),
    momentum: Math.round(momComponent),
    effectiveFocPenetration: Math.round(focComponent),
    energyRetention: Math.round(retentionComponent),
  };
  const effectiveWeights = {
    tuneQuality: Math.round(w.tune),
    momentum: Math.round(w.mom),
    effectiveFocPenetration: Math.round(w.foc),
    energyRetention: Math.round(w.ret),
  };
  const source = inputs.build.tuneQualitySource ?? 'default';
  const { diagnostic, weakestAxis } = buildDiagnostic(score, components, source, inputs.build, traj);
  const energyBudget = computeEnergyBudget(inputs.build, inputs.env.shotScenario, traj);

  return {
    score,
    tier: tierFromScore(score),
    components,
    effectiveWeights,
    source,
    trajectory: traj,
    diagnostic,
    weakestAxis,
    energyBudget,
  };
}

/**
 * Decompose the launch KE into the four loss components and the effective
 * KE at impact. The four losses, in order they occur during flight:
 *
 *   1. Drag loss — KE bled to air drag along flight path.
 *   2. Wind shear loss — KE bled to sideways shearing from wind-induced yaw
 *      at impact.
 *   3. Tune yaw loss — KE bled to launch-yaw shearing at impact (the moat).
 *   4. Deflection risk loss — expected KE lost to bone deflection given the
 *      shot scenario × head type combination.
 *
 * Effective KE at impact = launch KE × all four loss factors stacked.
 */
function computeEnergyBudget(
  build: Build,
  shotScenario: ShotScenario,
  traj: TrajectoryResult
): ForgeScoreResult['energyBudget'] {
  const launchKe = traj.keFtLb;
  const keAfterDrag = traj.keAtImpactFtLb;
  const dragLoss = launchKe - keAfterDrag;

  /* Wind-induced yaw vs total impact yaw — split losses */
  const launchYaw = Math.max(0, build.tuneQualityYawDeg);
  const totalImpactYaw = Math.max(0, traj.impactYawDeg);
  const windInducedYaw = Math.max(0, totalImpactYaw - launchYaw);

  /* Tune yaw loss: cos²(launchYaw) factor on retained KE */
  const tuneFactor = tuneQualityFactor(launchYaw);
  const tuneYawLoss = keAfterDrag * (1 - tuneFactor);
  const keAfterTune = keAfterDrag - tuneYawLoss;

  /* Wind shear loss: cos²(windInducedYaw) factor */
  const windFactor = Math.cos((windInducedYaw * Math.PI) / 180);
  const windShearLoss = keAfterTune * (1 - Math.max(0, windFactor * windFactor));
  const keAfterWind = keAfterTune - windShearLoss;

  /* Deflection risk loss */
  const pDeflect = deflectionProbability(shotScenario, build.pointType);
  const deflectionPenetration = DEFLECTION_OUTCOME_PENETRATION[build.pointType];
  const deflectionFactor = (1 - pDeflect) * 1.0 + pDeflect * deflectionPenetration;
  const deflectionRiskLoss = keAfterWind * (1 - deflectionFactor);
  const effectiveKe = keAfterWind - deflectionRiskLoss;

  return {
    launchKeFtLb: Math.round(launchKe * 10) / 10,
    dragLossFtLb: Math.round(dragLoss * 10) / 10,
    windShearLossFtLb: Math.round(windShearLoss * 10) / 10,
    tuneYawLossFtLb: Math.round(tuneYawLoss * 10) / 10,
    deflectionRiskLossFtLb: Math.round(deflectionRiskLoss * 10) / 10,
    effectiveKeAtImpactFtLb: Math.round(effectiveKe * 10) / 10,
  };
}

/**
 * Compose a one-sentence diagnostic that names the weakest component and
 * tells the user the next lever to move. This is the "why your score is
 * what it is" sentence — the thing competitors don't surface.
 */
function buildDiagnostic(
  score: number,
  components: { tuneQuality: number; momentum: number; effectiveFocPenetration: number; energyRetention: number },
  source: 'opencv' | 'slider' | 'default',
  build: Build,
  traj: TrajectoryResult
): { diagnostic: string; weakestAxis: ForgeScoreResult['weakestAxis'] } {
  /* Identify the weakest axis */
  const entries = [
    { key: 'tuneQuality' as const, value: components.tuneQuality, weight: 40 },
    { key: 'momentum' as const, value: components.momentum, weight: 25 },
    { key: 'effectiveFocPenetration' as const, value: components.effectiveFocPenetration, weight: 20 },
    { key: 'energyRetention' as const, value: components.energyRetention, weight: 15 },
  ];
  entries.sort((a, b) => a.value - b.value);
  const weakest = entries[0];

  /* Per-axis sentence patterns */
  let sentence = '';
  switch (weakest.key) {
    case 'tuneQuality': {
      if (source === 'default') {
        sentence = `Tune quality is the lowest axis (${weakest.value}). Currently assumed clean — measure from a flight photo in the Range Log for an accurate score.`;
      } else if (source === 'slider') {
        sentence = `Tune quality is the lowest axis (${weakest.value}, estimated from slider). Bareshaft-tune to reduce impact yaw — even 3° improvement adds ~6 Forge Score points.`;
      } else {
        sentence = `Tune quality is the lowest axis (${weakest.value}, measured). Impact yaw of ${traj.impactYawDeg.toFixed(1)}° is the limiter. Re-paper-tune; if you can drop it below 4°, the score climbs to roughly ${Math.min(100, score + 8)}.`;
      }
      break;
    }
    case 'momentum': {
      sentence = `Momentum is the lowest axis (${weakest.value}). At ${build.arrowGrains}gr you're under-massed for the shot. Try +50gr in the arrow build (heavier point or insert) — that typically adds 5-8 Forge Score points without changing speed much.`;
      break;
    }
    case 'effectiveFocPenetration': {
      if (build.fOCPercent < 11) {
        sentence = `Effective FOC penetration is the lowest axis (${weakest.value}). FOC at ${build.fOCPercent.toFixed(1)}% is below the 11-15% sweet spot — heavier point or lighter shaft will move the score.`;
      } else if (build.fOCPercent > 18) {
        sentence = `Effective FOC penetration is the lowest axis (${weakest.value}). At ${build.fOCPercent.toFixed(1)}% FOC you're past the diminishing-returns curve; deflection probability is dragging the score. Consider lighter point or different head category.`;
      } else {
        sentence = `Effective FOC penetration is the lowest axis (${weakest.value}). The deflection-probability matrix is hitting this build — check the shot scenario and head type combination.`;
      }
      break;
    }
    case 'energyRetention': {
      sentence = `Energy retention at distance is the lowest axis (${weakest.value}). At ${traj.tofMs}ms TOF and ${(traj.retainedKePct * 100).toFixed(0)}% KE retained at impact, this build is leaking energy to drag. Lower-offset vane geometry (DCA Sabre) is the cheapest fix.`;
      break;
    }
  }

  return { diagnostic: sentence, weakestAxis: weakest.key };
}

export { deflectionProbability, DEFLECTION_OUTCOME_PENETRATION } from './deflection';
